import { getCollectionProgress } from '../../services/collectionsService';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyProfile, getMyCoins, uploadAvatar, selectAvatar, clearSelectedAvatar, removeAvatar } from '../../services/authService';
import { WatchlistService } from '../../services/watchlistService';
import { startQuiz } from '../../services/quizService';
import LoadingSkeleton from '../../components/LoadingSkeleton/LoadingSkeleton';
import CoinBadge from '../../components/CoinBadge/CoinBadge';
import CoinModal from '../../components/CoinModal/CoinModal';
import ShareButtons from '../../components/ShareButtons/ShareButtons';
import styles from './MyProfile.module.css';
import { FoundingCollectorIcon, FoundingCollectorLabel } from '../../components/FoundingCollectorBadge/FoundingCollectorBadge';
import MyListsSection from '../../components/MyLists/MyListsSection';
import TopMoviesProfileSection from '../../components/TopMovies/TopMoviesProfileSection';
import RecentlyRatedSection from '../../components/RatedMovies/RecentlyRatedSection';
import RankingsProfileSection, { PROFILE_RANKINGS_LIMIT } from '../../components/Rankings/RankingsProfileSection';
import { RankingService } from '../../services/rankingService';
import { TitleRatingService } from '../../services/titleRatingService';
import { TopMoviesService } from '../../services/topMoviesService';
import AvatarCropModal from '../../components/AvatarCropModal/AvatarCropModal';
import AvatarPickerModal from '../../components/AvatarPicker/AvatarPickerModal';
import ProfilePictureModal from '../../components/AvatarPicker/ProfilePictureModal';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';

const PROFILE_NUDGE_DISMISSED_KEY = 'aluvo_profile_picture_nudge_dismissed';

export default function MyProfile() {
    const { user, logout, updateUserData } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [coins, setCoins] = useState([]);
    const [watchlist, setWatchlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [avatarUploading, setAvatarUploading] = useState(false);
    // Profile-image fields from the latest avatar change (overrides the loaded profile).
    const [localImage, setLocalImage] = useState(null); // { profileImage, uploadedAvatarUrl }
    const [pictureMenuOpen, setPictureMenuOpen] = useState(false);
    const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
    const [nudgeDismissed, setNudgeDismissed] = useState(() => {
        try { return localStorage.getItem(PROFILE_NUDGE_DISMISSED_KEY) === '1'; } catch { return false; }
    });
    const [cropSrc, setCropSrc] = useState(null); // object URL of the photo being cropped
    const [activeTab, setActiveTab] = useState('coins'); // 'coins' or 'watchlist'
    const [coinFilter, setCoinFilter] = useState('personal'); // 'personal' or 'legendary'
    const [watchlistFilter, setWatchlistFilter] = useState('all'); // 'all', 'movie', 'tv'
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [isCoinModalOpen, setIsCoinModalOpen] = useState(false);
    const [collections, setCollections] = useState([]);
    const [completedCollections, setCompletedCollections] = useState([]);
    const [quizLoading, setQuizLoading] = useState(false);
    // My Top Movies preview; undefined while loading, null when not created yet.
    const [topMovies, setTopMovies] = useState(undefined);
    // Recently Rated: undefined while loading. Fetched on every visit, so a
    // rating changed on a movie page shows here when the user comes back.
    const [recentRatings, setRecentRatings] = useState(undefined);
    const [recentRatingsError, setRecentRatingsError] = useState(false);
    const loadRecentRatings = useCallback(() => {
        let alive = true;
        setRecentRatingsError(false);
        TitleRatingService.myHistory({ sort: 'recent', limit: 10 })
            .then((page) => alive && setRecentRatings(page?.items ?? []))
            .catch(() => alive && setRecentRatingsError(true));
        return () => { alive = false; };
    }, []);
    useEffect(() => loadRecentRatings(), [loadRecentRatings]);
    // My Rankings preview (newest three, any visibility). Like Recently Rated it
    // is fetched on every visit, so creating, editing, renaming, making private
    // or deleting a ranking shows here when the user comes back.
    const [rankings, setRankings] = useState(undefined);
    const [rankingsError, setRankingsError] = useState(false);
    const loadRankings = useCallback(() => {
        let alive = true;
        setRankingsError(false);
        RankingService.listMine({ limit: PROFILE_RANKINGS_LIMIT })
            .then((page) => alive && setRankings(page?.items ?? []))
            .catch(() => alive && setRankingsError(true));
        return () => { alive = false; };
    }, []);
    useEffect(() => loadRankings(), [loadRankings]);
    useEffect(() => {
        let alive = true;
        TopMoviesService.getMine()
            .then((data) => alive && setTopMovies(data?.topMovies ?? null))
            .catch(() => alive && setTopMovies(null));
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        loadCollections();
    }, []);

    async function loadCollections() {
        try {
            const data = await getCollectionProgress();
            setCollections(data);
            const completed = data.filter((c) => c.completed);
            setCompletedCollections(completed);
        } catch (err) {
            console.error('Failed to load collections:', err);
        }
    }

    const load = useCallback(async () => {
        try {
            const [p, c, w] = await Promise.all([
                getMyProfile(),
                getMyCoins(),
                WatchlistService.getWatchlist()
            ]);
            console.log('Profile response:', p);
            console.log('Coins response:', c);
            console.log('Watchlist response:', w);

            setProfile(p);
            // The API returns coins array directly now
            const coinsArray = Array.isArray(c) ? c : [];
            console.log('Coins array:', coinsArray);
            setCoins(coinsArray);

            // Watchlist items
            const watchlistItems = w?.items || [];
            setWatchlist(watchlistItems);
        } catch (err) {
            console.error('Failed to load profile data:', err);
            // show empty states
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // The backend returns the account's resolved profile-image fields after every
    // change; keep the page and the signed-in user (navbar etc.) in sync with them.
    const applyProfileImage = useCallback((result) => {
        if (!result?.profileImage) return;
        setLocalImage({ profileImage: result.profileImage, uploadedAvatarUrl: result.uploaded_avatar_url ?? null });
        updateUserData?.({ avatarUrl: result.avatar_url ?? null, profileImage: result.profileImage });
    }, [updateUserData]);

    // Picking a file opens the crop modal; the upload happens when the crop is saved.
    const handlePhotoPicked = (file) => {
        setPictureMenuOpen(false);
        if (file.type && !file.type.startsWith('image/')) {
            alert('Please choose an image file.');
            return;
        }
        setCropSrc(URL.createObjectURL(file));
    };

    const openAvatarPicker = () => {
        setPictureMenuOpen(false);
        setAvatarPickerOpen(true);
        trackEvent(ANALYTICS_EVENTS.PROFILE_AVATAR_PICKER_OPENED, { source_screen: 'profile' });
    };

    const handleAvatarSelected = async (avatar) => {
        const result = await selectAvatar(avatar.id);
        applyProfileImage(result);
        setAvatarPickerOpen(false);
        trackEvent(ANALYTICS_EVENTS.PROFILE_AVATAR_SELECTED, {
            avatar_id: avatar.id, category: avatar.category, unlock_type: avatar.unlockType, source_screen: 'profile',
        });
    };

    const runPictureAction = async (action) => {
        setAvatarUploading(true);
        try {
            applyProfileImage(await action());
            setPictureMenuOpen(false);
        } catch (err) {
            alert(err.message || 'Could not update your profile picture.');
        } finally {
            setAvatarUploading(false);
        }
    };

    const handleUsePhoto = () => runPictureAction(clearSelectedAvatar);

    const handleRemovePicture = () => {
        if (!window.confirm('Remove your profile picture? You\'ll go back to the default avatar.')) return;
        const previousType = profileImage?.type ?? 'default';
        runPictureAction(async () => {
            const result = await removeAvatar();
            trackEvent(ANALYTICS_EVENTS.PROFILE_AVATAR_REMOVED, { previous_type: previousType, source_screen: 'profile' });
            return result;
        });
    };

    const dismissNudge = () => {
        setNudgeDismissed(true);
        try { localStorage.setItem(PROFILE_NUDGE_DISMISSED_KEY, '1'); } catch { /* storage unavailable */ }
    };

    const closeCrop = useCallback(() => setCropSrc(null), []);

    // Free the previous object URL whenever the crop source changes or the page unmounts.
    useEffect(() => () => { if (cropSrc) URL.revokeObjectURL(cropSrc); }, [cropSrc]);

    const handleCroppedAvatar = async (file) => {
        setAvatarUploading(true);
        try {
            const updated = await uploadAvatar(file);
            applyProfileImage(updated);
            closeCrop();
            trackEvent(ANALYTICS_EVENTS.PROFILE_PHOTO_UPLOADED, { source_screen: 'profile' });
        } catch (err) {
            alert(err.message || 'Upload failed.');
        } finally {
            setAvatarUploading(false);
        }
    };

    const handleLogout = async () => {
        if (!window.confirm('Are you sure you want to sign out?')) return;
        await logout();
        navigate('/', { replace: true });
    };

    const handleRemoveFromWatchlist = async (itemId) => {
        if (!window.confirm('Remove this from your watchlist?')) return;
        try {
            await WatchlistService.removeFromWatchlist(itemId);
            setWatchlist(watchlist.filter(item => item.id !== itemId));
        } catch (err) {
            alert('Failed to remove from watchlist');
        }
    };

    const handleCoinClick = (coin) => {
        setSelectedCoin(coin);
        setIsCoinModalOpen(true);
    };

    const handleCloseCoinModal = () => {
        if (quizLoading) return; // prevent closing while quiz is generating
        setIsCoinModalOpen(false);
        setSelectedCoin(null);
    };

    const handleUpgradeCoin = async () => {
        if (!selectedCoin || quizLoading) return;

        setQuizLoading(true);
        try {
            const quizData = await startQuiz(
                selectedCoin.tmdbId,
                selectedCoin.mediaKind,
                selectedCoin.seasonNumber || null
            );

            if (!quizData.quizReady) {
                const reason = quizData.reason;
                if (reason === 'legendary_cooldown') {
                    const retryAt = quizData.availableAt
                        ? new Date(quizData.availableAt).toLocaleString()
                        : 'soon';
                    alert(`Legendary attempt not ready yet. Available at: ${retryAt}`);
                } else if (reason === 'legendary_achieved') {
                    alert('You already have Legendary for this title!');
                } else {
                    alert(quizData.message || 'This challenge is not available right now.');
                }
                return;
            }

            // Quiz is ready — close modal and navigate
            setIsCoinModalOpen(false);
            setSelectedCoin(null);
            navigate('/quiz', {
                state: {
                    quizData,
                    kind: selectedCoin.mediaKind,
                    seasonNumber: selectedCoin.seasonNumber || null,
                    mediaTitle: selectedCoin.mediaTitle,
                    tmdbId: selectedCoin.tmdbId,
                },
            });
        } catch (err) {
            console.error('Failed to start quiz:', err);
            alert(err.message || "We couldn't prepare your challenge. Please try again.");
        } finally {
            setQuizLoading(false);
        }
    };

    // The backend resolves the active image (curated > uploaded > default).
    const profileImage = localImage?.profileImage
        ?? profile?.profileImage
        ?? { type: (profile?.avatar_url ?? user?.avatarUrl) ? 'uploaded' : 'default', url: profile?.avatar_url ?? user?.avatarUrl ?? null };
    const uploadedAvatarUrl = localImage ? localImage.uploadedAvatarUrl : (profile?.uploaded_avatar_url ?? null);
    const resolvedAvatar = profileImage.url;
    const displayName = profile?.display_name ?? user?.displayName ?? user?.username;
    const isFoundingCollector = Boolean(profile?.isFoundingCollector ?? user?.isFoundingCollector);
    const username = profile?.username ?? user?.username;
    const coinCount = profile?.stats?.badgeCount ?? coins.length; // ✅ Use badgeCount from API first

    const sortedCoins = [...coins].sort((a, b) => new Date(b.earnedAt ?? 0) - new Date(a.earnedAt ?? 0));

    // Separate personal and legendary coins
    const personalCoins    = sortedCoins.filter(coin => coin.sourceType === 'media' && coin.rarity !== 'legendary');
    const legendaryCoins   = sortedCoins.filter(coin =>
        (coin.sourceType === 'media' && coin.rarity === 'legendary') ||
        coin.sourceType === 'series_mastery'
    );
    const collectionCoins  = sortedCoins.filter(coin => coin.sourceType === 'collection');

    // Filter watchlist based on selected filter
    const filteredWatchlist = watchlist.filter(item => {
        if (watchlistFilter === 'all') return true;
        return item.media?.kind === watchlistFilter;
    });

    const publicProfileUrl = username ? `${window.location.origin}/u/${username}` : window.location.origin;
    const publicProfileShareTitle = `Check out @${username}'s verified movie & TV profile on Aluvo — ${coinCount} verified coins earned!`;

    console.log('Rendering with coins:', sortedCoins);

    if (loading) return <ProfileSkeleton />;

    return (
        <main className={styles.page}>
            <div className={styles.inner}>

                {/* ── Screen header ── */}
                <div className={styles.screenHeader}>
                    <h1 className={styles.screenTitle}>Profile</h1>
                    <button
                        className={styles.settingsBtn}
                        onClick={() => navigate('/settings')}
                        title="Settings"
                    >
                        ⚙️
                    </button>
                </div>

                {/* ── User header ── */}
                <div className={styles.userHeader}>
                    {/* ... existing avatar / stats / share content unchanged ... */}
                    <button
                        type="button"
                        className={styles.avatarLabel}
                        title="Change profile picture"
                        aria-label="Change profile picture"
                        onClick={() => setPictureMenuOpen(true)}
                        disabled={avatarUploading}
                    >
                        <div className={`${styles.avatarWrap} ${avatarUploading ? styles.avatarLoading : ''}`}>
                            {resolvedAvatar ? (
                                <img src={resolvedAvatar} alt={displayName} className={styles.avatar} />
                            ) : (
                                <div className={styles.avatarPlaceholder}>
                                    {(displayName?.[0] ?? '?').toUpperCase()}
                                </div>
                            )}
                            <div className={styles.avatarOverlay}>
                                {avatarUploading ? <span className={styles.spinnerDark} /> : '📷'}
                            </div>
                        </div>
                    </button>

                    <div className={styles.userInfo}>
                        <h2 className={styles.displayName}>
                            {displayName}
                            <FoundingCollectorIcon isFoundingCollector={isFoundingCollector} size="md" />
                        </h2>
                        <p className={styles.usernameTag}>@{username}</p>
                        <FoundingCollectorLabel isFoundingCollector={isFoundingCollector} />
                        {profile?.bio && <p className={styles.bio}>{profile.bio}</p>}
                    </div>

                    <div className={styles.statsRow}>
                        <div className={styles.statBox}>
                            <span className={styles.statIcon}>🪙</span>
                            <span className={styles.statValue}>{coinCount}</span>
                            <span className={styles.statLabel}>Coins</span>
                        </div>
                        <div className={styles.statDivider} />
                        <Link to={`/user/${username}/followers`} className={styles.statBox}>
                            <span className={styles.statIcon}>👥</span>
                            <span className={styles.statValue}>{profile?.stats?.followersCount ?? 0}</span>
                            <span className={styles.statLabel}>Followers</span>
                        </Link>
                        <div className={styles.statDivider} />
                        <Link to={`/user/${username}/following`} className={styles.statBox}>
                            <span className={styles.statIcon}>➡️</span>
                            <span className={styles.statValue}>{profile?.stats?.followingCount ?? 0}</span>
                            <span className={styles.statLabel}>Following</span>
                        </Link>
                    </div>

                    <div className={styles.publicShareBlock}>
                        <Link to={`/u/${username}`} className={styles.publicProfileLink}>
                            View / share public trophy case →
                        </Link>
                        {username && (
                            <ShareButtons
                                url={publicProfileUrl}
                                title={publicProfileShareTitle}
                                label="Share profile:"
                            />
                        )}
                    </div>

                    {/* Optional, after activation only — never part of first-coin onboarding. */}
                    {profileImage.type === 'default' && coinCount > 0 && !nudgeDismissed && (
                        <div className={styles.profileNudge}>
                            <div className={styles.profileNudgeText}>
                                <strong>Make your profile yours</strong>
                                <span>Choose a movie-inspired avatar or upload your own photo.</span>
                            </div>
                            <button type="button" className={styles.profileNudgeBtn} onClick={() => setPictureMenuOpen(true)}>
                                Pick a picture
                            </button>
                            <button type="button" className={styles.profileNudgeClose} onClick={dismissNudge} aria-label="Dismiss">
                                ✕
                            </button>
                        </div>
                    )}

                    {/* How It Works */}
                    <Link
                        to="/how-it-works"
                        className={styles.howItWorksLink}
                    >
                        <span className={styles.howItWorksIcon}>❓</span>
                        <span className={styles.howItWorksLabel}>How It Works</span>
                        <span className={styles.howItWorksChevron}>›</span>
                    </Link>
                </div>

            </div>

            <div className={styles.topMoviesWrap}>
                <TopMoviesProfileSection topMovies={topMovies} isOwner loading={topMovies === undefined} />
                <RankingsProfileSection
                    items={rankings}
                    error={rankingsError}
                    onRetry={loadRankings}
                    isOwner
                    seeAllTo="/me/rankings"
                    sourceScreen="my_profile"
                    className={styles.recentlyRated}
                />
                <RecentlyRatedSection
                    items={recentRatings}
                    error={recentRatingsError}
                    onRetry={loadRecentRatings}
                    isOwner
                    seeAllTo="/me/ratings"
                    sourceScreen="my_profile"
                    className={styles.recentlyRated}
                />
            </div>

            {/* ── Sticky tab bar + filters ── */}
            <div className={styles.stickySection}>
                <div className={styles.stickyInner}>

                    {/* Tabs */}
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'coins' ? styles.activeTab : ''}`}
                            onClick={() => setActiveTab('coins')}
                        >
                            🪙 My Coins ({coinCount})
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'watchlist' ? styles.activeTab : ''}`}
                            onClick={() => setActiveTab('watchlist')}
                        >
                            📺 My Watchlist ({watchlist.length})
                        </button>
                    </div>

                    {/* Coins filters */}
                    {activeTab === 'coins' && (
                        <div className={styles.stickyFilters}>
                            <div className={styles.sectionHead}>
                                <h2 className={styles.sectionTitle}>🪙 My Coins</h2>
                                <span className={styles.sectionCount}>{coinCount} {coinCount === 1 ? 'coin' : 'coins'}</span>
                            </div>
                            <div className={styles.filterPills}>
                                <button
                                    className={`${styles.filterPill} ${coinFilter === 'personal' ? styles.filterPillActive : ''}`}
                                    onClick={() => setCoinFilter('personal')}
                                >
                                    🪙 Personal Coins ({personalCoins.length})
                                </button>
                                <button
                                    className={`${styles.filterPill} ${coinFilter === 'legendary' ? styles.filterPillActive : ''}`}
                                    onClick={() => setCoinFilter('legendary')}
                                >
                                    💎 Legendary Coins ({legendaryCoins.length})
                                </button>
                                <button
                                    className={`${styles.filterPill} ${coinFilter === 'collection' ? styles.filterPillActive : ''}`}
                                    onClick={() => setCoinFilter('collection')}
                                >
                                    🎴 Collection Coins ({collectionCoins.length})
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Watchlist filters */}
                    {activeTab === 'watchlist' && (
                        <div className={styles.stickyFilters}>
                            <div className={styles.sectionHead}>
                                <h2 className={styles.sectionTitle}>📺 My Watchlist</h2>
                                <span className={styles.sectionCount}>{watchlist.length} {watchlist.length === 1 ? 'item' : 'items'}</span>
                            </div>
                            {watchlist.length > 0 && (
                                <div className={styles.filterPills}>
                                    <button
                                        className={`${styles.filterPill} ${watchlistFilter === 'all' ? styles.filterPillActive : ''}`}
                                        onClick={() => setWatchlistFilter('all')}
                                    >
                                        All
                                    </button>
                                    <button
                                        className={`${styles.filterPill} ${watchlistFilter === 'movie' ? styles.filterPillActive : ''}`}
                                        onClick={() => setWatchlistFilter('movie')}
                                    >
                                        🎬 Movies
                                    </button>
                                    <button
                                        className={`${styles.filterPill} ${watchlistFilter === 'tv' ? styles.filterPillActive : ''}`}
                                        onClick={() => setWatchlistFilter('tv')}
                                    >
                                        📺 TV Shows
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Scrollable content beneath sticky section ── */}
            <div className={styles.contentArea}>

                {/* ── Coins Tab content ── */}
                {activeTab === 'coins' && (
                    <section className={styles.section}>

                        {/* Personal Coins View */}
                        {coinFilter === 'personal' && (
                            <>
                                {personalCoins.length === 0 ? (
                                    <div className={styles.emptyCoins}>
                                        <span className={styles.emptyIcon}>🪙</span>
                                        <h3 className={styles.emptyTitle}>No personal coins yet</h3>
                                        <p className={styles.emptySubtitle}>
                                            Pass a challenge after watching a movie or season to earn your first verified coin.
                                        </p>
                                        <Link to="/discover" className={styles.emptyBtn}>Find Something to Watch</Link>
                                    </div>
                                ) : (
                                    <div className={styles.coinGrid}>
                                        {personalCoins.map((coin) => (
                                            <CoinBadge
                                                key={coin.id}
                                                badge={coin}
                                                size="md"
                                                showLabel={true}
                                                onClick={handleCoinClick}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Legendary Coins View */}
                        {coinFilter === 'legendary' && (
                            <div className={styles.legendarySection}>
                                {legendaryCoins.length === 0 ? (
                                    <div className={styles.emptyCoins}>
                                        <span className={styles.emptyIcon}>💎</span>
                                        <h3 className={styles.emptyTitle}>No legendary coins yet</h3>
                                        <p className={styles.emptySubtitle}>
                                            Complete collections to unlock Legendary coins. Each collection rewards you with a special coin!
                                        </p>
                                        <button
                                            className={styles.viewCollectionsBtn}
                                            onClick={() => navigate('/collections')}
                                        >
                                            View Collections
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.coinGrid}>
                                        {legendaryCoins.map((coin) => (
                                            <CoinBadge
                                                key={coin.id}
                                                badge={coin}
                                                size="md"
                                                showLabel={true}
                                                onClick={handleCoinClick}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Collection Coins View */}
                        {coinFilter === 'collection' && (
                            <div className={styles.legendarySection}>
                                {collectionCoins.length === 0 ? (
                                    <div className={styles.emptyCoins}>
                                        <span className={styles.emptyIcon}>🎴</span>
                                        <h3 className={styles.emptyTitle}>No collection coins yet</h3>
                                        <p className={styles.emptySubtitle}>
                                            These coins are earned by viewing collections. Explore a collection to start earning!
                                        </p>
                                        <button
                                            className={styles.viewCollectionsBtn}
                                            onClick={() => navigate('/collections')}
                                        >
                                            View Collections
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.collectionsCallout}>
                                            <p className={styles.collectionsCalloutText}>
                                                {completedCollections.length > 0
                                                    ? `You've completed ${completedCollections.length} ${completedCollections.length === 1 ? 'collection' : 'collections'}!`
                                                    : 'Keep collecting to unlock more legendary coins'
                                                }
                                            </p>
                                            <button
                                                className={styles.viewCollectionsBtn}
                                                onClick={() => navigate('/collections')}
                                            >
                                                View All Collections
                                            </button>
                                        </div>
                                        <div className={styles.coinGrid}>
                                            {collectionCoins.map((coin) => (
                                                <CoinBadge
                                                    key={coin.id}
                                                    badge={coin}
                                                    size="md"
                                                    showLabel={true}
                                                    onClick={handleCoinClick}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </section>
                )}

                {/* ── Watchlist Tab content ── */}
                {activeTab === 'watchlist' && (
                    <section className={styles.section}>
                        {/* ── My Lists rail (custom lists) sits above the Watchlist so it is discoverable ── */}
                        <MyListsSection source="watchlist" />

                        {watchlist.length === 0 ? (
                            <div className={styles.emptyWatchlist}>
                                <span className={styles.emptyIcon}>📺</span>
                                <h3 className={styles.emptyTitle}>Your watchlist is empty</h3>
                                <p className={styles.emptySubtitle}>
                                    Save movies and shows you want to watch later.
                                </p>
                                <Link to="/discover" className={styles.emptyBtn}>Discover Content</Link>
                            </div>
                        ) : filteredWatchlist.length === 0 ? (
                            <div className={styles.emptyWatchlist}>
                                <span className={styles.emptyIcon}>🔍</span>
                                <h3 className={styles.emptyTitle}>No {watchlistFilter === 'movie' ? 'movies' : 'TV shows'} found</h3>
                                <p className={styles.emptySubtitle}>
                                    Try selecting a different filter
                                </p>
                            </div>
                        ) : (
                            <div className={styles.watchlistGrid}>
                                {filteredWatchlist.map((item) => (
                                    <div key={item.id} className={styles.watchlistCard}>
                                        <Link
                                            to={`/movie/${item.media?.kind || 'movie'}/${item.media?.tmdb_id}`}
                                            className={styles.watchlistPosterWrap}
                                        >
                                            {item.media?.poster_url ? (
                                                <img
                                                    src={item.media.poster_url}
                                                    alt={item.media.title}
                                                    className={styles.watchlistPoster}
                                                />
                                            ) : (
                                                <div className={styles.watchlistPosterPlaceholder}>
                                                    {item.media?.kind === 'tv' ? '📺' : '🎬'}
                                                </div>
                                            )}
                                            {item.status && (
                                                <div className={styles.statusBadge}>
                                                    {item.status === 'watched' ? '✓' : '👁'}
                                                </div>
                                            )}
                                        </Link>
                                        <div className={styles.watchlistInfo}>
                                            <Link
                                                to={`/movie/${item.media?.kind || 'movie'}/${item.media?.tmdb_id}`}
                                                className={styles.watchlistTitle}
                                            >
                                                {item.media?.title || 'Untitled'}
                                            </Link>
                                            <button
                                                className={styles.removeBtn}
                                                onClick={() => handleRemoveFromWatchlist(item.id)}
                                                title="Remove from watchlist"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

            </div>

            {pictureMenuOpen && (
                <ProfilePictureModal
                    profileImage={profileImage}
                    uploadedAvatarUrl={uploadedAvatarUrl}
                    name={displayName}
                    busy={avatarUploading}
                    onClose={() => setPictureMenuOpen(false)}
                    onChooseAvatar={openAvatarPicker}
                    onPhotoPicked={handlePhotoPicked}
                    onUsePhoto={handleUsePhoto}
                    onRemove={handleRemovePicture}
                />
            )}

            {avatarPickerOpen && (
                <AvatarPickerModal
                    currentAvatarId={profileImage.type === 'curated' ? profileImage.avatarId : null}
                    onCancel={() => setAvatarPickerOpen(false)}
                    onSave={handleAvatarSelected}
                    onLockedTap={(avatar) => trackEvent(ANALYTICS_EVENTS.PROFILE_AVATAR_LOCKED_TAPPED, {
                        avatar_id: avatar.id, category: avatar.category, unlock_type: avatar.unlockType, source_screen: 'profile',
                    })}
                />
            )}

            {cropSrc && (
                <AvatarCropModal
                    key={cropSrc}
                    imageSrc={cropSrc}
                    onCancel={closeCrop}
                    onSave={handleCroppedAvatar}
                />
            )}

            {/* Coin Detail Modal */}
            <CoinModal
                isOpen={isCoinModalOpen}
                onClose={handleCloseCoinModal}
                coin={selectedCoin}
                onUpgrade={handleUpgradeCoin}
                quizLoading={quizLoading}
            />
        </main>
    );
}

function ProfileSkeleton() {
    return (
        <div className={styles.page} style={{ paddingTop: 'calc(var(--nav-height) + 40px)' }}>
            <div className={styles.inner}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <LoadingSkeleton width="72px" height="72px" borderRadius="50%" />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <LoadingSkeleton height="24px" width="160px" />
                        <LoadingSkeleton height="14px" width="100px" />
                    </div>
                </div>
                <LoadingSkeleton height="80px" borderRadius="12px" />
                <LoadingSkeleton height="200px" borderRadius="12px" />
            </div>
        </div>
    );
}
